import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { I18nService } from 'i18n';
import { CommunityService } from '../../../services/community.service';
import {
  CommunitySearchPostHit,
  CommunitySearchResponse,
  CommunitySearchThreadHit,
  CommunitySearchType,
  CommunityUserBrief,
  communityErrorKey
} from '../../../models/community.models';
import { CommunityAvatarComponent } from '../../../components/community-avatar/community-avatar.component';
import { CommunityBadgesComponent } from '../../../components/community-badges/community-badges.component';

/**
 * `community/search?q=` — full-text search across threads, posts and people
 * (§13), always through the same visibility filters as the lists (a search
 * hit can never surface something the viewer could not otherwise open).
 */
@Component({
  selector: 'app-community-search-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslocoModule, CommunityAvatarComponent, CommunityBadgesComponent],
  templateUrl: './community-search-page.component.html',
  styleUrl: './community-search-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunitySearchPageComponent implements OnInit, OnDestroy {
  readonly types: CommunitySearchType[] = ['all', 'threads', 'posts', 'people'];

  query = '';
  type = signal<CommunitySearchType>('all');
  page = signal(1);
  loading = signal(false);
  error = signal<string | null>(null);
  tooShort = signal(false);
  result = signal<CommunitySearchResponse | null>(null);

  private routeSub?: Subscription;
  private static readonly PER_PAGE = 20;

  constructor(private communityService: CommunityService, private i18n: I18nService, private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe(params => {
      this.query = params.get('q') ?? '';
      const t = params.get('type') as CommunitySearchType | null;
      this.type.set(t && this.types.includes(t) ? t : 'all');
      this.page.set(Number(params.get('page')) || 1);
      this.run();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  threadLink(id: number): string {
    return `/${this.lang}/community/${id}`;
  }

  memberLink(id: number): string {
    return `/${this.lang}/community/u/${id}`;
  }

  submit(): void {
    this.navigate({ q: this.query.trim(), type: this.type(), page: 1 });
  }

  selectType(type: CommunitySearchType): void {
    this.navigate({ q: this.query.trim(), type, page: 1 });
  }

  goToPage(page: number): void {
    this.navigate({ q: this.query.trim(), type: this.type(), page });
  }

  private navigate(query: { q: string; type: CommunitySearchType; page: number }): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: query.q || null, type: query.type === 'all' ? null : query.type, page: query.page > 1 ? query.page : null }
    });
  }

  private run(): void {
    const q = this.query.trim();
    this.error.set(null);
    this.tooShort.set(false);
    if (q.length < 2) {
      this.tooShort.set(q.length > 0);
      this.result.set(null);
      return;
    }
    this.loading.set(true);
    this.communityService.search(q, this.type(), this.page(), CommunitySearchPageComponent.PER_PAGE).subscribe({
      next: res => {
        this.loading.set(false);
        this.result.set(res);
      },
      error: err => {
        this.loading.set(false);
        this.result.set(null);
        if (err?.status === 422 && err?.error?.code === 'community_query_too_short') {
          this.tooShort.set(true);
        } else {
          this.error.set(communityErrorKey(err));
        }
      }
    });
  }

  threadHits(): CommunitySearchThreadHit[] {
    return this.result()?.threads?.data ?? [];
  }

  postHits(): CommunitySearchPostHit[] {
    return this.result()?.posts?.data ?? [];
  }

  peopleHits(): CommunityUserBrief[] {
    return this.result()?.people?.data ?? [];
  }

  totalPages(): number {
    const r = this.result();
    if (!r) return 1;
    const total = r.threads?.total_count ?? r.posts?.total_count ?? 0;
    return Math.max(1, Math.ceil(total / CommunitySearchPageComponent.PER_PAGE));
  }
}
